/* eslint-disable max-len, no-nested-ternary */

import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CaretRightOutlined, PauseOutlined } from '@ant-design/icons';
import { Dropdown, Skeleton, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { convertDaysHrsMinsToMins, convertDaysHrsMinsSecsToHMS, convertMinsToHrsMins, renderTime } from 'utils/string.utils';
import useTimer from './useTimer';
import styles from './Timer.module.less';
import TimerContainer from './TimerContainer';
import TasksDropdown from '../TasksDropdown';

interface TimerDropdownProps {
  task_id?: string;
  projectId?: string;
  globalActiveTimer?: boolean;
  timerContent?: boolean;
  taskStatus?: string;
  needsAlert?: boolean;
  showUserSelection?: boolean;
  onAddEntry?: any;
  insideTasksDropdown?: boolean;
}

const TimerDropdown: FC<TimerDropdownProps> = ({
  task_id,
  projectId,
  globalActiveTimer = false,
  timerContent,
  taskStatus,
  needsAlert,
  showUserSelection,
  onAddEntry,
  insideTasksDropdown = false,
}) => {
  const { data: dataLogs } = useSelector((store: any) => store.activeLogs);
  const [visible, setVisible] = useState(false);

  const {
    minutes,
    hours,
    days,
    seconds,
    isRunning,
    current,
    totalTime,
    form,
    loading,
    loadingTotal,
    saving,
    onChangeTimer,
    onTimeFocusChange,
    startTimer,
    pauseTimer,
    updateTimer,
    resetTimer,
    addEntry,
  } = useTimer({
    task_id,
    globalActiveTimer,
    timerContent,
    onAddEntry,
    onClose: () => setVisible(false),
  });

  const isTimerActive = isRunning && current;
  const now = dayjs().tz('America/New_York');
  const isBusinessHours = now.hour() >= 8 && now.hour() < 17;

  const pausedTimeLogs = useMemo(() => {
    if (!dataLogs) return 0;
    return dataLogs.filter((log: any) => log.status === 'paused').length;
  }, [dataLogs]);

  let timerStatus: 'running' | 'paused' | 'notRunning' = 'notRunning';
  if (isTimerActive) {
    timerStatus = 'running';
  } else if (pausedTimeLogs > 0 || (saving && !isRunning)) {
    timerStatus = 'paused';
  }

  const showSkeleton = globalActiveTimer && dataLogs === null && !insideTasksDropdown;
  const showStatusLabel = !showSkeleton && (timerStatus === 'paused' || (timerStatus === 'notRunning' && isBusinessHours && !saving)) && !insideTasksDropdown;
  const showTimerControls = !showSkeleton && timerStatus === 'running';

  if (insideTasksDropdown) {
    return (
      <>
        {visible && (
          <div
            className={styles.modal}
            onClick={(e) => {
              e.stopPropagation();
              setVisible(false);
              if (!saving) {
                updateTimer();
              }
            }}
          />
        )}
        <Dropdown
          overlay={
            <TimerContainer
              style={{ width: 480 }}
              dataLog={current}
              totalTime={totalTime.userTotal}
              form={form}
              projectId={projectId}
              modalStatus={taskStatus}
              needsAlert={needsAlert}
              loading={loading || saving}
              loadingTotal={loadingTotal}
              globalActiveTimer={globalActiveTimer}
              showUserSelection={showUserSelection ? 'task' : undefined}
              requiredDescription
              onAddEntry={addEntry}
              onChangeTimer={onChangeTimer}
              onTimeFocusChange={onTimeFocusChange}
              onResetTimer={resetTimer}
              onCancel={() => {
                setVisible(false);
                updateTimer();
              }}
            />
          }
          placement="bottomRight"
          visible={visible}
        >
          <div
            className={`${styles.timer} ${saving || loading ? styles.saving : ''}
				${globalActiveTimer ? styles.global : ''}`}
          >
            {isRunning ? (
              <Tooltip title="Pause timelog" placement="bottom">
                <PauseOutlined className="task-inline-timer-button-icon" onClick={pauseTimer} />
              </Tooltip>
            ) : (
              <Tooltip title="Start timelog" placement="bottom">
                <CaretRightOutlined className="task-inline-timer-button-icon" onClick={startTimer} />
              </Tooltip>
            )}
            &nbsp;
            <span
              className={`${styles.value} ${isRunning ? styles.running : ''}`}
              onClick={() => setVisible(true)}
            >
              {!globalActiveTimer && !timerContent ? (
                <>
                  {convertMinsToHrsMins(convertDaysHrsMinsToMins(days, hours, minutes))} &bull;{' '}
                  {convertMinsToHrsMins(totalTime.taskTotal)}
                </>
              ) : globalActiveTimer ? (
                convertDaysHrsMinsSecsToHMS(days, hours, minutes, seconds)
              ) : (
                renderTime(convertMinsToHrsMins(convertDaysHrsMinsToMins(days, hours, minutes)))
              )}
            </span>
          </div>
        </Dropdown>
      </>
    );
  }

  if (showSkeleton) {
    return (
      <div className={styles.timerSkeleton}>
        <Skeleton active paragraph={false} />
      </div>
    );
  }

  if (!showStatusLabel && !showTimerControls) {
    return null;
  }

  return (
    <>
      {visible && (
        <div
          className={styles.modal}
          onClick={(e) => {
            e.stopPropagation();
            setVisible(false);
            if (!saving) {
              updateTimer();
            }
          }}
        />
      )}
      <div id="timer-div-container" className={`${styles.divTimerContainer} ${!showSkeleton ? styles[timerStatus] : ''}`}>
        {showStatusLabel && (
          <>
            <span className={styles.statusLabel}>
              {timerStatus === 'notRunning' ? 'Not running' : 'Paused'}
            </span>
            <TasksDropdown iconSize="sm" />
          </>
        )}
        {showTimerControls && (
          <>
            {isRunning ? (
              <Tooltip title="Pause timelog" placement="bottom">
                <PauseOutlined className="task-inline-timer-button-icon" onClick={pauseTimer} />
              </Tooltip>
            ) : (
              <Tooltip title="Start timelog" placement="bottom">
                <CaretRightOutlined onClick={startTimer} />
              </Tooltip>
            )}

            <Dropdown
              overlay={
                <TimerContainer
                  style={{ width: 480 }}
                  dataLog={current}
                  totalTime={totalTime.userTotal}
                  form={form}
                  projectId={projectId}
                  modalStatus={taskStatus}
                  needsAlert={needsAlert}
                  loading={loading || saving}
                  loadingTotal={loadingTotal}
                  globalActiveTimer={globalActiveTimer}
                  showUserSelection={showUserSelection ? 'task' : undefined}
                  requiredDescription
                  onAddEntry={addEntry}
                  onChangeTimer={onChangeTimer}
                  onTimeFocusChange={onTimeFocusChange}
                  onResetTimer={resetTimer}
                  onCancel={() => {
                    setVisible(false);
                    updateTimer();
                  }}
                />
              }
              placement="bottomRight"
              visible={visible}
            >
              <div
                className={`${styles.timer} ${saving || loading ? styles.saving : ''}
					${globalActiveTimer ? styles.global : ''}`}
              >
                <span
                  className={`${styles.value} ${isRunning ? styles.running : ''}`}
                  onClick={() => setVisible(true)}
                >
                {!globalActiveTimer && !timerContent ? (
                  <>
                    {convertMinsToHrsMins(convertDaysHrsMinsToMins(days, hours, minutes))} &bull;{' '}
                    {convertMinsToHrsMins(totalTime.taskTotal)}
                  </>
                ) : globalActiveTimer ? (
                  convertDaysHrsMinsSecsToHMS(days, hours, minutes, seconds)
                ) : (
                  renderTime(convertMinsToHrsMins(convertDaysHrsMinsToMins(days, hours, minutes)))
                )}
              </span>
              </div>
            </Dropdown>

            <TasksDropdown iconSize="sm" />
          </>
        )}
      </div>
    </>
  );
};

export default TimerDropdown;
